import axios from 'axios';

// Configuração base do axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://relatorioapi.helderporto.com.br';

// Criar instância do axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000, // 90 segundos
  headers: {
    'Content-Type': 'application/json',
  },
  // FastAPI espera listas como chaves repetidas: product_ids=a&product_ids=b
  // (evita serialização com colchetes: product_ids[]=a)
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      const entries = Object.entries(params || {});

      for (const [key, value] of entries) {
        if (value === undefined || value === null || value === '') continue;

        if (Array.isArray(value)) {
          for (const item of value) {
            if (item === undefined || item === null || item === '') continue;
            searchParams.append(key, String(item));
          }
        } else {
          searchParams.append(key, String(value));
        }
      }

      return searchParams.toString();
    },
  },
});

// Interceptor para adicionar token de autenticação
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e renovar token automaticamente
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Refresh single-flight: evita várias chamadas concorrentes a /refresh
    // (ex.: várias requests recebem 401 ao mesmo tempo).
    if (!apiClient.__refreshPromise) {
      apiClient.__refreshPromise = null;
    }

    const isAuthRefreshRequest =
      typeof originalRequest?.url === 'string' &&
      originalRequest.url.includes('/api/v1/auth/refresh');

    // Se o erro for 401 (não autorizado) e não for uma tentativa de renovação
    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthRefreshRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (refreshToken) {
          // Tentar renovar o token (single-flight)
          if (!apiClient.__refreshPromise) {
            apiClient.__refreshPromise = axios
              .post(`${API_BASE_URL}/api/v1/auth/refresh`, {
                refresh_token: refreshToken,
              })
              .then((response) => {
                const { access_token, refresh_token: newRefreshToken } = response.data;

                // Salvar novos tokens
                localStorage.setItem('token', access_token);
                localStorage.setItem('refreshToken', newRefreshToken);

                return access_token;
              })
              .finally(() => {
                apiClient.__refreshPromise = null;
              });
          }

          const access_token = await apiClient.__refreshPromise;
          
          // Repetir a requisição original com o novo token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Se não conseguir renovar, limpar credenciais e notificar o app
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.dispatchEvent(new Event('auth:sessionExpired'));
        return Promise.reject(refreshError);
      }

      // Sem refresh token: tratar como sessão expirada
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      window.dispatchEvent(new Event('auth:sessionExpired'));
    }

    return Promise.reject(error);
  }
);

// Função para fazer requisições GET
export const get = async (url, params = {}) => {
  try {
    const response = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para fazer requisições POST
export const post = async (url, data = {}, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para fazer requisições PUT
export const put = async (url, data = {}) => {
  try {
    const response = await apiClient.put(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para fazer requisições DELETE
export const del = async (url) => {
  try {
    const response = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para fazer upload de arquivos
export const upload = async (url, file, onProgress = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    const response = await apiClient.post(url, formData, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para download de arquivos
export const download = async (url, filename) => {
  try {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });

    // Criar link para download
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    throw handleApiError(error);
  }
};

// Função para tratar erros da API
const handleApiError = (error) => {
  if (error.response) {
    // Erro de resposta do servidor
    const { status, data } = error.response;
    
    const stringifyDetail = (detail) => {
      if (detail === null || detail === undefined) return '';
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        return detail
          .map((d) => {
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object') {
              const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
              const msg = d.msg ? String(d.msg) : JSON.stringify(d);
              return loc ? `${loc}: ${msg}` : msg;
            }
            return String(d);
          })
          .filter(Boolean)
          .join(' | ');
      }
      if (typeof detail === 'object') {
        if (detail.msg) return String(detail.msg);
        return JSON.stringify(detail);
      }
      return String(detail);
    };

    let message = 'Erro interno do servidor';

    if (data?.detail) {
      message = stringifyDetail(data.detail) || 'Erro na requisição';
    } else if (data?.message) {
      message = data.message;
    } else if (status === 400) {
      message = 'Dados inválidos fornecidos';
    } else if (status === 401) {
      message = 'Não autorizado. Faça login novamente.';
    } else if (status === 403) {
      message = 'Acesso negado';
    } else if (status === 404) {
      message = 'Recurso não encontrado';
    } else if (status === 422) {
      message = 'Dados de entrada inválidos';
    } else if (status >= 500) {
      message = 'Erro interno do servidor. Tente novamente mais tarde.';
    }

    return {
      message,
      status,
      data,
      isApiError: true,
    };
  } else if (error.request) {
    // Erro de rede
    return {
      message: 'Erro de conexão. Verifique sua internet e tente novamente.',
      status: 0,
      data: null,
      isNetworkError: true,
    };
  } else {
    // Erro desconhecido
    return {
      message: error.message || 'Erro desconhecido',
      status: 0,
      data: null,
      isUnknownError: true,
    };
  }
};

// Função para verificar se a API está disponível
export const checkApiHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`, {
      timeout: 5000,
    });
    return {
      isAvailable: true,
      status: response.data.status,
      data: response.data,
    };
  } catch (error) {
    return {
      isAvailable: false,
      error: error.message,
    };
  }
};

// Exportar instância do axios para uso direto quando necessário
export { apiClient };

// Configurações e constantes
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  TIMEOUT: 90000,
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
};
